import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { ChatBubbleLeftRightIcon, UserIcon } from '@heroicons/react/24/outline';
import { getNodeColors } from '../utils/nodeColors';

const TelegramMessageNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const nodeColors = getNodeColors('telegram_message');

  const botToken = data.config?.botToken || 'Nincs beállítva';
  const chatId = data.config?.chatId || 'Nincs beállítva';
  const message = data.config?.message || 'Nincs üzenet';
  const parseMode = data.config?.parseMode || 'HTML';
  
  // Üzenet előnézet (első 60 karakter)
  const messagePreview = message.length > 60 ? `${message.substring(0, 60)}...` : message;
  
  // Chat ID formázás (ha hosszú, rövidítjük)
  const chatIdDisplay = chatId.length > 20 ? `${chatId.substring(0, 17)}...` : chatId;
  
  // Bot token státusz (csak jelezzük, hogy van-e)
  const hasToken = botToken && botToken !== 'Nincs beállítva';

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r ${nodeColors.gradient.from} ${nodeColors.gradient.to} border-2 transition-all duration-200 ${
      selected ? nodeColors.border.selected + ' shadow-xl scale-105' : nodeColors.border.default
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className={`text-xs ${nodeColors.text.secondary} truncate mt-1`}>
              {data.description}
            </div>
          )}
          
          {/* Bot Status */}
          <div className="flex items-center mt-1 space-x-1">
            <span className="text-sm text-white">🤖</span>
            <span className={`text-xs ${nodeColors.text.secondary}`}>
              {hasToken ? 'Bot konfigurálva' : 'Bot token hiányzik'}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-700' : hasToken ? 'bg-green-400' : 'bg-yellow-400'
          }`} />
        </div>
      </div>

      {/* Configuration preview */}
      <div className="mt-2 space-y-1">
        {/* Chat ID */}
        <div className={`text-xs ${nodeColors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded`}>
          <strong>Chat:</strong>
          <span className="ml-1 truncate">{chatIdDisplay}</span>
        </div>
        
        {/* Parse Mode */}
        <div className={`text-xs ${nodeColors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded flex items-center justify-between`}>
          <div>
            <strong>Formátum:</strong>
            <span className="ml-1">{parseMode}</span>
          </div>
          {parseMode === 'HTML' && <span className="text-xs">🏷️</span>}
          {parseMode === 'Markdown' && <span className="text-xs">📝</span>}
        </div>
        
        {/* Message Preview */}
        <div className={`text-xs ${nodeColors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded`}>
          <strong>Üzenet:</strong>
          <div className="truncate mt-1 opacity-90">
            {messagePreview}
          </div>
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-700 bg-opacity-30 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Delivery status */}
      {data.executionData && (
        <div className="mt-2 text-xs text-blue-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              data.executionData.success ? 'bg-green-400' : 'bg-red-400'
            }`} />
            <span>
              {data.executionData.success ? 'Elküldve' : 'Sikertelen'}
            </span>
          </div>
          {data.executionData.messageId && (
            <div className="font-mono text-xs mt-1">
              ID: {data.executionData.messageId}
            </div>
          )}
          {data.executionData.chatId && (
            <div className="text-xs mt-1">
              Chat: {data.executionData.chatId}
            </div>
          )}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className={`w-3 h-3 ${nodeColors.handle} border-2 border-white`}
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className={`w-3 h-3 ${nodeColors.handle} border-2 border-white`}
      />
    </div>
  );
};

export default TelegramMessageNode;
